import { useState, useEffect } from 'react'
import { Box, Rating, Tabs, Tab, Grid, TextField, MenuItem, FormControl, InputLabel, Select, Typography, Chip } from '@mui/material'
import { Add, Edit, Delete, Star, Assessment } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal } from '@components/common'
import { VendorForm } from '@components/vendors'
import { toast } from 'react-toastify'
import api from '@services/api'

const vendorColumns = [
  { field: 'code', headerName: 'Code', sortable: true, width: 100 },
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'contact_person', headerName: 'Contact Person', sortable: true },
  { field: 'email', headerName: 'Email', sortable: true },
  { field: 'phone', headerName: 'Phone', sortable: true },
  {
    field: 'vendor_type',
    headerName: 'Type',
    sortable: true,
    renderCell: (value) => value?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-',
  },
  {
    field: 'rating',
    headerName: 'Rating',
    renderCell: (value) => <Rating value={value} size="small" readOnly />,
  },
  {
    field: 'is_active',
    headerName: 'Status',
    renderCell: (value) => (
      <StatusChip status={value ? 'active' : 'inactive'} />
    ),
  },
]

const performanceColumns = [
  {
    field: 'evaluation_date',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
  { field: 'vendor_name', headerName: 'Vendor', sortable: true },
  {
    field: 'rating',
    headerName: 'Overall Rating',
    renderCell: (value) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Rating value={value} size="small" readOnly />
        <Typography variant="body2">({value}/5)</Typography>
      </Box>
    ),
  },
  {
    field: 'quality_score',
    headerName: 'Quality',
    renderCell: (value) => value ? (
      <Chip
        label={`${value}/5`}
        size="small"
        color={value >= 4 ? 'success' : value >= 3 ? 'warning' : 'error'}
      />
    ) : '-',
  },
  {
    field: 'cost_accuracy',
    headerName: 'Cost Accuracy',
    renderCell: (value) => value ? (
      <Chip
        label={`${value}/5`}
        size="small"
        color={value >= 4 ? 'success' : value >= 3 ? 'warning' : 'error'}
      />
    ) : '-',
  },
  {
    field: 'response_time_hours',
    headerName: 'Response Time',
    renderCell: (value) => value ? `${value} hrs` : '-',
  },
  { field: 'evaluated_by_name', headerName: 'Evaluated By' },
]

const VendorsList = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [vendors, setVendors] = useState([])
  const [performances, setPerformances] = useState([])
  const [workOrders, setWorkOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [performanceFormOpen, setPerformanceFormOpen] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [selectedPerformance, setSelectedPerformance] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteType, setDeleteType] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [performanceFormData, setPerformanceFormData] = useState({
    vendor: '',
    work_order: '',
    rating: 3,
    quality_score: 3,
    cost_accuracy: 3,
    response_time_hours: '',
    comments: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [vendorsRes, performancesRes, workOrdersRes] = await Promise.all([
        api.get('/vendors/'),
        api.get('/vendors/performance/'),
        api.get('/maintenance/work-orders/?status=completed'),
      ])
      setVendors(vendorsRes.data.results || vendorsRes.data)
      setPerformances(performancesRes.data.results || performancesRes.data)
      setWorkOrders(workOrdersRes.data.results || workOrdersRes.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAddClick = () => {
    if (activeTab === 0) {
      setSelectedVendor(null)
      setFormOpen(true)
    } else {
      setSelectedPerformance(null)
      setPerformanceFormData({
        vendor: '',
        work_order: '',
        rating: 3,
        quality_score: 3,
        cost_accuracy: 3,
        response_time_hours: '',
        comments: '',
      })
      setPerformanceFormOpen(true)
    }
  }

  const handleFormSuccess = () => {
    fetchData()
    setSelectedVendor(null)
  }

  const handleEditVendor = (vendor) => {
    setSelectedVendor(vendor)
    setFormOpen(true)
  }

  const handleEditPerformance = (performance) => {
    setSelectedPerformance(performance)
    setPerformanceFormData({
      vendor: performance.vendor || '',
      work_order: performance.work_order || '',
      rating: performance.rating || 3,
      quality_score: performance.quality_score || 3,
      cost_accuracy: performance.cost_accuracy || 3,
      response_time_hours: performance.response_time_hours || '',
      comments: performance.comments || '',
    })
    setPerformanceFormOpen(true)
  }

  const handleSavePerformance = async () => {
    if (!performanceFormData.vendor || !performanceFormData.rating) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedPerformance) {
        await api.patch(`/vendors/performance/${selectedPerformance.id}/`, performanceFormData)
        toast.success('Performance review updated')
      } else {
        await api.post('/vendors/performance/', performanceFormData)
        toast.success('Performance review added')
      }
      setPerformanceFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving performance:', error)
      toast.error('Failed to save performance review')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = (item, type) => {
    if (type === 'vendor') {
      setSelectedVendor(item)
      setDeleteType('vendor')
    } else {
      setSelectedPerformance(item)
      setDeleteType('performance')
    }
    setDeleteModalOpen(true)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      if (deleteType === 'vendor') {
        await api.delete(`/vendors/${selectedVendor.id}/`)
        toast.success('Vendor deleted')
      } else {
        await api.delete(`/vendors/performance/${selectedPerformance.id}/`)
        toast.success('Performance review deleted')
      }
      fetchData()
      setDeleteModalOpen(false)
      setSelectedVendor(null)
      setSelectedPerformance(null)
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error('Failed to delete')
    } finally {
      setDeleting(false)
    }
  }

  const vendorActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditVendor(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'vendor')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const performanceActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditPerformance(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'performance')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const getAddButtonText = () => {
    return activeTab === 0 ? 'Add Vendor' : 'Add Review'
  }

  const getDeleteItemName = () => {
    if (deleteType === 'vendor') return selectedVendor?.name
    return `review for ${selectedPerformance?.vendor_name}`
  }

  // Calculate average scores for vendors
  const getVendorStats = () => {
    const stats = {}
    performances.forEach((p) => {
      if (!stats[p.vendor]) {
        stats[p.vendor] = { total: 0, count: 0 }
      }
      stats[p.vendor].total += p.rating
      stats[p.vendor].count += 1
    })
    return stats
  }

  return (
    <Box>
      <PageHeader
        title="Vendors"
        subtitle="Manage service providers, suppliers, and performance reviews"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendors' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAddClick}>
            {getAddButtonText()}
          </Button>
        }
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="Vendors" icon={<Star />} iconPosition="start" />
          <Tab label="Performance Reviews" icon={<Assessment />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <DataTable
            columns={[...vendorColumns, vendorActionsColumn]}
            data={vendors}
            loading={loading}
            searchPlaceholder="Search vendors..."
            emptyMessage="No vendors found"
          />
        )}

        {activeTab === 1 && (
          <DataTable
            columns={[...performanceColumns, performanceActionsColumn]}
            data={performances}
            loading={loading}
            searchPlaceholder="Search performance reviews..."
            emptyMessage="No performance reviews found"
          />
        )}
      </Card>

      <VendorForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedVendor(null)
        }}
        vendor={selectedVendor}
        onSuccess={handleFormSuccess}
      />

      {/* Performance Review Form Modal */}
      <Modal
        open={performanceFormOpen}
        onClose={() => setPerformanceFormOpen(false)}
        title={selectedPerformance ? 'Edit Performance Review' : 'Add Performance Review'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setPerformanceFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePerformance} loading={saving}>
              {selectedPerformance ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={performanceFormData.vendor}
                onChange={(e) => setPerformanceFormData({ ...performanceFormData, vendor: e.target.value })}
                label="Vendor"
              >
                {vendors.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Related Work Order (Optional)</InputLabel>
              <Select
                value={performanceFormData.work_order}
                onChange={(e) => setPerformanceFormData({ ...performanceFormData, work_order: e.target.value })}
                label="Related Work Order (Optional)"
              >
                <MenuItem value="">None</MenuItem>
                {workOrders.map((wo) => (
                  <MenuItem key={wo.id} value={wo.id}>{wo.work_order_number} - {wo.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography component="legend">Overall Rating *</Typography>
            <Rating
              value={performanceFormData.rating}
              onChange={(_, newValue) => setPerformanceFormData({ ...performanceFormData, rating: newValue })}
              size="large"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography component="legend">Quality Score</Typography>
            <Rating
              value={performanceFormData.quality_score}
              onChange={(_, newValue) => setPerformanceFormData({ ...performanceFormData, quality_score: newValue })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography component="legend">Cost Accuracy</Typography>
            <Rating
              value={performanceFormData.cost_accuracy}
              onChange={(_, newValue) => setPerformanceFormData({ ...performanceFormData, cost_accuracy: newValue })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Response Time (hours)"
              type="number"
              inputProps={{ step: '0.5', min: 0 }}
              value={performanceFormData.response_time_hours}
              onChange={(e) => setPerformanceFormData({ ...performanceFormData, response_time_hours: e.target.value })}
              helperText="How quickly did the vendor respond?"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Comments"
              value={performanceFormData.comments}
              onChange={(e) => setPerformanceFormData({ ...performanceFormData, comments: e.target.value })}
              multiline
              rows={3}
              placeholder="Describe the vendor's performance..."
            />
          </Grid>
        </Grid>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={`Delete ${deleteType === 'vendor' ? 'Vendor' : 'Performance Review'}`}
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete "{getDeleteItemName()}"?
      </Modal>
    </Box>
  )
}

export default VendorsList
